import type { FC, ReactNode } from 'react';
import { motion } from 'framer-motion';

interface BuildingCardProps {
  title: string;
  description?: string;
  owned: number;
  productionPerUnit: string;
  totalProduction: string;
  price: string;
  disabled?: boolean;
  onPurchase: () => void;
  progressBar?: ReactNode;
  icon?: ReactNode;
}

export const BuildingCard: FC<BuildingCardProps> = ({
  title,
  description,
  owned,
  productionPerUnit,
  totalProduction,
  price,
  disabled = false,
  onPurchase,
  progressBar,
  icon
}) => {
  return (
    <motion.div
      whileHover={!disabled ? { scale: 1.02 } : undefined}
      whileTap={!disabled ? { scale: 0.98 } : undefined}
      className={`rounded-xl border-2 p-4 transition-all ${
        disabled
          ? 'border-white/10 bg-white/5 opacity-60'
          : 'border-white/15 bg-white/10 hover:border-primary/60'
      }`}
    >
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            {icon && <span className="text-2xl">{icon}</span>}
            <h3 className="text-lg font-bold text-amber-50">{title}</h3>
          </div>
          {description && <p className="text-xs text-amber-200/80">{description}</p>}
        </div>
        {owned > 0 && (
          <motion.span
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="rounded-full bg-primary/20 px-2 py-1 text-xs font-semibold text-primary"
          >
            ×{owned}
          </motion.span>
        )}
      </div>

      {progressBar && <div className="mb-3">{progressBar}</div>}

      <div className="mb-3 flex items-center justify-between text-xs text-amber-100/80">
        <span>
          Produces{' '}
          <span className="font-semibold text-yellow-200">{productionPerUnit}/s</span> each
        </span>
        <span className="text-green-300">
          Total:{' '}
          <span className="font-semibold text-green-200">
            {totalProduction}
            /s
          </span>
        </span>
      </div>

      <motion.button
        type="button"
        onClick={onPurchase}
        disabled={disabled}
        className={`w-full rounded-lg px-4 py-2 text-sm font-semibold transition-all ${
          disabled
            ? 'cursor-not-allowed bg-gray-500/70 text-gray-200/60'
            : 'bg-gradient-neon text-black shadow-neon hover:shadow-neon-lg'
        }`}
      >
        🪙 {price}
      </motion.button>
    </motion.div>
  );
};

